import { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { clanCreate, clanJoin, listClans, type PublicClan } from "../lib/api";
import { TASKS } from "../data/tasks";
import clanPromoPhoto from "../assets/tasks/logo-story.jpeg";
import { useHub } from "../hooks/useHub";
import TaskList from "../components/TaskList";
import MemberList from "../components/MemberList";
import TabBar from "../components/TabBar";
import { useT } from "../i18n";
import { errorMessage } from "../i18n/errors";

const EMOJIS = ["🔥", "🐯", "🦅", "🐺", "⚡", "🦁", "🐉", "💪"];

const FIELD =
  "w-full bg-input text-black rounded-[14px] px-4 py-3 text-base placeholder:text-grey/70 outline-none focus:ring-2 focus:ring-violet";

function BackButton({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="w-9 h-9 rounded-full bg-white/[0.06] flex items-center justify-center active:bg-white/10 transition-colors"
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );
}

export default function Clan() {
  const nav = useNavigate();
  const loc = useLocation();
  const { t } = useT();
  const [params] = useSearchParams();
  const phone = params.get("phone") || "";
  const q = `?phone=${encodeURIComponent(phone)}`;

  // Режим: /clan/join → вступление, /clan/create → создание, /clan → меню.
  const mode: "create" | "join" | "menu" = loc.pathname.endsWith("/join")
    ? "join"
    : loc.pathname.endsWith("/create")
      ? "create"
      : "menu";

  // Меню кланов (вкладка «Кланы») читает состояние хаба.
  const { hub } = useHub(mode === "menu" && phone ? phone : null);

  const [name, setName] = useState("");
  const [motto, setMotto] = useState("");
  const [emoji, setEmoji] = useState("");
  const [code, setCode] = useState("");

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Публичный список всех кланов фестиваля (только на вкладке «Кланы»).
  const [allClans, setAllClans] = useState<PublicClan[] | null>(null);
  useEffect(() => {
    if (mode !== "menu" || !phone) return;
    let alive = true;
    listClans()
      .then((r) => {
        if (alive) setAllClans(r.clans);
      })
      .catch(() => {
        /* список некритичен — молча игнорируем */
      });
    return () => {
      alive = false;
    };
  }, [mode, phone]);

  if (!phone) {
    return (
      <div className="min-h-dvh flex items-center justify-center px-6 text-muted text-sm">
        {t("clan.noPhone")}
      </div>
    );
  }

  async function create() {
    setError("");
    if (!name.trim()) return setError(t("clan.errNoName"));
    // Латиница, как и ники: название светится в лидерборде и панелях стаффа.
    if (!/^[A-Za-z0-9_ ]{2,24}$/.test(name.trim())) return setError(t("clan.errNameLatin"));
    setLoading(true);
    try {
      await clanCreate(name.trim(), motto.trim() || undefined, emoji || undefined);
      nav(`/hub${q}`);
    } catch (e: any) {
      setError(errorMessage(e, t, "clan.errCreateFailed"));
    } finally {
      setLoading(false);
    }
  }

  async function join() {
    setError("");
    if (!code.trim()) return setError(t("clan.errNoCode"));
    setLoading(true);
    try {
      await clanJoin(code.trim().toUpperCase());
      nav(`/hub${q}`);
    } catch (e: any) {
      setError(errorMessage(e, t, "clan.errJoinFailed"));
    } finally {
      setLoading(false);
    }
  }

  // ===== Menu mode (Кланы tab) =====
  if (mode === "menu") {
    const clan = hub?.clan ?? null;
    return (
      <div className="min-h-dvh max-w-md mx-auto px-[22px] pt-4 pb-28">
        <header className="flex items-center gap-3 pt-1.5 mb-4">
          <BackButton onClick={() => nav(`/hub${q}`)} label={t("clan.back")} />
          <div className="flex-1 min-w-0">
            <h1 className="text-[21px] font-extrabold tracking-tight leading-[1.1]">{t("hub.clan.title")}</h1>
            <p className="text-[13px] text-muted mt-[3px]">{t("hub.subtitle")}</p>
          </div>
        </header>

        <section className="relative bg-violet-card rounded-[22px] p-5 overflow-hidden shadow-violet-lg">
          {/* Промо без клана — с фото задания под фиолетовой заливкой (как на главной) */}
          {!clan && (
            <>
              <img src={clanPromoPhoto} alt="" className="absolute inset-0 w-full h-full object-cover" />
              <div
                className="absolute inset-0"
                style={{ background: "linear-gradient(180deg, rgba(124,92,252,0.55) 0%, rgba(98,56,238,0.88) 55%, rgba(91,52,232,0.96) 100%)" }}
              />
            </>
          )}
          <span className="absolute -right-[30px] -bottom-[50px] w-[150px] h-[150px] rounded-full bg-white/10" />
          {clan ? (
            <div className="relative">
              <div className="flex items-center gap-3 mb-4">
                {clan.emoji && <span className="text-3xl leading-none">{clan.emoji}</span>}
                <div className="flex-1 min-w-0">
                  <h2 className="text-[19px] font-extrabold text-white leading-tight">{clan.name}</h2>
                  {clan.motto && <p className="text-white/80 text-sm mt-0.5">{clan.motto}</p>}
                </div>
                <span className="text-xs font-bold text-white bg-white/[0.18] rounded-full px-2.5 py-1 shrink-0">
                  {clan.code}
                </span>
              </div>
              {!clan.ready && (
                <div className="rounded-[14px] bg-white/10 px-4 py-3 mb-4 text-sm text-white/85">
                  {t("hub.clan.notReady", { count: clan.memberCount, code: clan.code })}
                </div>
              )}
              <div className="rounded-[16px] bg-black/15 p-4 mb-3 [&_.text-muted]:text-white/70 [&_.bg-card]:bg-white/10 [&_.border-line]:border-white/10">
                <TaskList tasks={clan.tasks} onOpen={(key) => nav(`/task/${key}${q}`)} />
              </div>
              <div className="rounded-[16px] bg-black/15 p-4 [&_.text-muted]:text-white/70 [&_.bg-card]:bg-white/10 [&_.border-line]:border-white/10">
                <MemberList members={clan.members} />
              </div>
            </div>
          ) : (
            <div className="relative">
              <h2 className="text-[19px] font-extrabold text-white mb-2">{t("hub.clan.title")}</h2>
              <p className="text-white/85 text-sm leading-[1.55] mb-[18px]">{t("hub.clan.body")}</p>
              <div className="flex gap-2.5">
                <button
                  type="button"
                  onClick={() => nav(`/clan/create${q}`)}
                  className="flex-1 h-[50px] rounded-[14px] bg-white text-violet-deepest font-extrabold text-[15px] active:opacity-90 transition-opacity"
                >
                  {t("hub.clan.create")}
                </button>
                <button
                  type="button"
                  onClick={() => nav(`/clan/join${q}`)}
                  className="flex-1 h-[50px] rounded-[14px] bg-white/10 ring-1 ring-white/45 text-white font-bold text-[15px] active:bg-white/20 transition-colors"
                >
                  {t("hub.clan.join")}
                </button>
              </div>
            </div>
          )}
        </section>

        {/* Задания фестиваля для тех, кто ещё без клана (в клане их показывает TaskList с прогрессом) */}
        {!clan && (
          <section className="mt-3.5">
            <h2 className="text-[15px] font-bold text-white mb-1">{t("clan.tasksTitle")}</h2>
            <p className="text-[13px] text-muted mb-2.5">{t("clan.tasksIntro")}</p>
            <div className="flex flex-col gap-2">
              {TASKS.map((task, i) => (
                <button
                  key={task.key}
                  type="button"
                  onClick={() => nav(`/task/${task.key}${q}`)}
                  className="w-full text-left flex items-center gap-3 bg-card border border-line-2 rounded-[14px] px-3.5 py-3 active:bg-white/[0.04] transition-colors"
                >
                  <span className="w-6 h-6 rounded-full bg-violet-t16 text-violet-lighter text-[12px] font-extrabold flex items-center justify-center shrink-0">
                    {i + 1}
                  </span>
                  <span className="flex-1 text-[13px] text-white/85 leading-[1.45]">{t(task.titleKey)}</span>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="shrink-0 opacity-60">
                    <path d="M9 6l6 6-6 6" stroke="#86868E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Все кланы фестиваля (публично, без кодов приглашения) */}
        {allClans && (
          <section className="mt-3.5">
            <h2 className="text-[15px] font-bold text-white mb-2.5">{t("clan.list.title")}</h2>
            {allClans.length === 0 ? (
              <p className="text-muted text-sm">{t("clan.list.empty")}</p>
            ) : (
              <div className="flex flex-col gap-2.5">
                {allClans.map((c) => {
                  const mine = clan?.id === c.id;
                  return (
                    <div
                      key={c.id}
                      className={`flex items-center gap-3 bg-card border rounded-[16px] p-3.5 ${
                        mine ? "border-violet/60" : "border-line-2"
                      }`}
                    >
                      <span className="w-10 h-10 rounded-[12px] bg-violet-t14 flex items-center justify-center text-xl shrink-0">
                        {c.emoji || "🛡️"}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-[15px] font-extrabold text-white truncate">{c.name}</span>
                          {mine && (
                            <span className="text-[10px] font-bold text-violet-lighter bg-violet-t14 px-1.5 py-0.5 rounded-md shrink-0 whitespace-nowrap">
                              {t("clan.list.yours")}
                            </span>
                          )}
                        </div>
                        {c.motto && <div className="text-xs text-muted truncate mt-0.5">{c.motto}</div>}
                        <div className="text-xs text-muted mt-1">
                          {t("clan.list.members", { count: c.memberCount })} ·{" "}
                          {t("clan.list.tasks", { done: c.tasksDone, total: c.tasksTotal })}
                        </div>
                      </div>
                      {c.completed && (
                        <span className="text-[11px] font-bold text-success bg-success/[0.12] px-2 py-0.5 rounded-md shrink-0 whitespace-nowrap">
                          {t("clan.list.completed")}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        )}

        <TabBar active="clans" phone={phone} />
      </div>
    );
  }

  // ===== Create / Join forms =====
  return (
    <div className="min-h-dvh flex flex-col px-[22px] py-8 max-w-md mx-auto">
      <header className="mb-6 flex items-center gap-3">
        <BackButton onClick={() => nav(`/hub${q}`)} label={t("clan.back")} />
        <h1 className="text-2xl font-extrabold tracking-tight">
          {mode === "create" ? t("clan.createTitle") : t("clan.joinTitle")}
        </h1>
      </header>

      <div className="flex-1 flex flex-col gap-4">
        {mode === "create" ? (
          <>
            <label className="flex flex-col gap-2">
              <span className="text-sm text-white/80">{t("clan.nameLabel")}</span>
              <input
                value={name}
                onChange={(e) => setName(e.target.value.replace(/[^A-Za-z0-9_ ]/g, "").slice(0, 24))}
                placeholder={t("clan.namePlaceholder")}
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                className={FIELD}
              />
            </label>
            <label className="flex flex-col gap-2">
              <span className="text-sm text-white/80">{t("clan.mottoLabel")}</span>
              <input
                value={motto}
                onChange={(e) => setMotto(e.target.value)}
                placeholder={t("clan.mottoPlaceholder")}
                className={FIELD}
              />
            </label>
            <div className="flex flex-col gap-2">
              <span className="text-sm text-white/80">{t("clan.emojiLabel")}</span>
              <div className="flex flex-wrap gap-2">
                {EMOJIS.map((e) => (
                  <button
                    key={e}
                    type="button"
                    onClick={() => setEmoji(emoji === e ? "" : e)}
                    className={`w-12 h-12 rounded-[14px] text-2xl flex items-center justify-center transition-colors ${
                      emoji === e ? "bg-violet-grad shadow-violet" : "bg-white/[0.06] active:bg-white/10"
                    }`}
                  >
                    {e}
                  </button>
                ))}
              </div>
            </div>
          </>
        ) : (
          <label className="flex flex-col gap-2">
            <span className="text-sm text-white/80">{t("clan.codeLabel")}</span>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder={t("clan.codePlaceholder")}
              autoCapitalize="characters"
              className={`${FIELD} tracking-widest placeholder:tracking-normal`}
            />
            <span className="text-muted text-xs">{t("clan.codeHint")}</span>
          </label>
        )}

        {error && <p className="text-sm text-red-400">{error}</p>}
      </div>

      <button
        type="button"
        onClick={mode === "create" ? create : join}
        disabled={loading}
        className="w-full h-14 rounded-[14px] bg-violet-grad text-white font-extrabold text-base active:opacity-90 transition-opacity disabled:opacity-60 mt-6 shadow-violet-btn"
      >
        {loading
          ? mode === "create"
            ? t("clan.creating")
            : t("clan.joining")
          : mode === "create"
            ? t("clan.create")
            : t("clan.join")}
      </button>
    </div>
  );
}
